import { useQuery } from "@tanstack/react-query";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useAuthContext } from "../contexts/AuthContext";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { TabView, TabPanel } from "primereact/tabview";
import { useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Badge } from "primereact/badge";
import VehicleHandleForm from "./assets/VehicleHandleForm";
import PropertyHandleForm from "./assets/PropertyHandleForm";
import StockHandleForm from "./assets/StockHandleForm";
import CriptoHandleForm from "./assets/CriptoHandleForm";
import FundHandleForm from "./assets/FundHandleForm";
import DebentureHandleForm from "./assets/DebentureHandleForm";
import PrivateBondsHandleForm from "./assets/PrivateBondsHandleForm";
import TreasureBoundsHandleForm from "./assets/TreasureBoundsHandleForm";
import SavingAccountHandleForm from "./assets/SavingAccountHandleForm";
import CheckingAccoundHandleForm from "./assets/CheckingAccountHandleForm";
import CurrencyHandleForm from "./assets/CurrencyHandleForm";
import CustomizedHandleForm from "./assets/CustomizedHandleForm";
import { 
    IExtractedProperty, 
    IExtractedVehicle, 
    IExtractedInvestment, 
    IExtractedData 
} from "../models/extractedData";
import type { IDirpfImportMeta } from "../commons/dirpfImportMeta";
import {
    dirpfImportIsComplete,
    dirpfImportNeedsAttention,
    dirpfImportStatusTooltip,
} from "../commons/dirpfImportMeta";

const DirpfImportStatusCell = ({ meta }: { meta?: IDirpfImportMeta | null }) => {
    if (meta == null) {
        return (
            <span
                className="inline-flex align-items-center justify-content-center text-500"
                title={dirpfImportStatusTooltip(undefined)}
            >
                <i className="pi pi-minus-circle" />
            </span>
        );
    }
    if (dirpfImportIsComplete(meta)) {
        return (
            <span
                className="inline-flex align-items-center justify-content-center text-green-600"
                title={dirpfImportStatusTooltip(meta)}
            >
                <i className="pi pi-check-circle text-xl" />
            </span>
        );
    }
    if (dirpfImportNeedsAttention(meta)) {
        return (
            <span
                className="inline-flex align-items-center justify-content-center text-yellow-600"
                title={dirpfImportStatusTooltip(meta)}
            >
                <i className="pi pi-exclamation-triangle text-xl" />
            </span>
        );
    }
    return (
        <span className="text-500" title={dirpfImportStatusTooltip(meta)}>
            <i className="pi pi-question-circle" />
        </span>
    );
};

interface ITaxImportDialogProps {
    importTaxOpen: boolean;
    setImportTaxOpen: (isOpen: boolean) => void;
}

type AssetFormType = 
    | "vehicle" 
    | "property" 
    | "stock" 
    | "crypto" 
    | "fund" 
    | "debenture" 
    | "private_bonds" 
    | "treasury" 
    | "savings" 
    | "checking"
    | "currency"
    | "customized";

interface ISelectedAssetToConfirm {
    type: AssetFormType;
    data: IExtractedVehicle | IExtractedProperty | IExtractedInvestment;
}

const mapAssetTypeToFormType = (assetType: string): AssetFormType | null => {
    switch (assetType) {
        case "ST": return "stock";
        case "CM": return "crypto";
        case "IF": return "fund";
        case "DB": return "debenture";
        case "PB": return "private_bonds";
        case "TB": return "treasury";
        case "SA": return "savings";
        case "CA": return "checking";
        case "CY": return "currency";
        case "CS": return "customized";
        default: return null;
    }
};

const getFormTitle = (type: AssetFormType): string => {
    switch (type) {
        case "vehicle": return "Confirmar Veículo";
        case "property": return "Confirmar Imóvel";
        case "stock": return "Confirmar Ação";
        case "crypto": return "Confirmar Criptomoeda";
        case "fund": return "Confirmar Fundo de Investimento";
        case "debenture": return "Confirmar Debênture";
        case "private_bonds": return "Confirmar Renda Fixa";
        case "treasury": return "Confirmar Tesouro Direto";
        case "savings": return "Confirmar Poupança";
        case "checking": return "Confirmar Conta Corrente";
        case "currency": return "Confirmar Moeda";
        case "customized": return "Confirmar Ativo Personalizado";
        default: return "Confirmar Ativo";
    }
};

interface ITaxImportStep {
    step: string;
    step_name: string;
    started_at: string;
    completed_at: string | null;
    duration_seconds: number | null;
    status: string;
    error_message: string | null;
    details: Record<string, unknown> | null;
}

interface ITaxImportStats {
    extraction_source: string;
    assets_created: number;
    assets_updated: number;
    assets_skipped: number;
    properties_created: number;
    properties_updated: number;
    properties_skipped: number;
    vehicles_created: number;
    vehicles_skipped: number;
    errors: string[];
    extracted_data?: IExtractedData;
}

interface ITaxImport {
    id: string;
    user_id: string;
    filename: string;
    s3_key: string;
    status: string;
    created_at: string;
    started_at: string;
    completed_at: string;
    error_message: string | null;
    file_size: number;
    steps: ITaxImportStep[];
    stats: ITaxImportStats;
}

const MOCK_DATA: { import_statuses: ITaxImport[] } = {
    import_statuses: [
        {
            id: "69b156086e78448ee70127c4",
            user_id: "68de78fa1a6d4c40982ec861",
            filename: "04698726913-IRPF-A-2025-2024-DEC.pdf",
            s3_key: "dirpf-dev/68de78fa1a6d4c40982ec861/04698726913-IRPF-A-2025-2024-DEC.pdf",
            status: "completed",
            created_at: "2026-03-11T11:46:16.221000",
            started_at: "2026-03-11T11:46:25.431000",
            completed_at: "2026-03-11T11:46:43.388000",
            stats: {
                extraction_source: "extraction",
                assets_created: 15,
                assets_updated: 0,
                assets_skipped: 0,
                properties_created: 3,
                properties_updated: 0,
                properties_skipped: 0,
                vehicles_created: 2,
                vehicles_skipped: 0,
                errors: [],
                extracted_data: {
                    properties: [
                        {
                            property_category: 1,
                            property_type: 1,
                            property_type_desc: "Apartamento",
                            property_purchase_date: "2022-03-18",
                            property_zip_code: "88108-300",
                            property_city: "SAO JOSE",
                            property_state: "SC",
                            property_place: "RUA JOAO THEODORO DA SILVA",
                            property_number: "150",
                            property_complement: "AP 202 B",
                            property_district: "ROCADO",
                            property_area: 113.4,
                            property_qtd_bedroom: 3,
                            property_qtd_bathroom: 2,
                            property_qtd_parking_spaces: 2,
                            property_purchase_value: 485000.00,
                            property_alias: "Apartamento - RUA JOAO THEODORO DA SILVA, ROCADO"
                        },
                        {
                            property_category: 1,
                            property_type: 2,
                            property_type_desc: "Casa",
                            property_purchase_date: "2019-08-15",
                            property_zip_code: "88101-280",
                            property_city: "FLORIANOPOLIS",
                            property_state: "SC",
                            property_place: "RUA VEREADOR MARIO COELHO PIRES",
                            property_number: "305",
                            property_complement: "",
                            property_district: "CAMPINAS",
                            property_area: 180.5,
                            property_qtd_bedroom: 4,
                            property_qtd_bathroom: 3,
                            property_qtd_parking_spaces: 3,
                            property_purchase_value: 750000.00,
                            property_alias: "Casa - RUA VEREADOR MARIO COELHO PIRES, CAMPINAS"
                        },
                        {
                            property_category: 2,
                            property_type: 9,
                            property_type_desc: "Sala comercial",
                            property_purchase_date: "2021-02-10",
                            property_zip_code: "88015-200",
                            property_city: "FLORIANOPOLIS",
                            property_state: "SC",
                            property_place: "AV BEIRA MAR NORTE",
                            property_number: "2500",
                            property_complement: "SALA 1205",
                            property_district: "CENTRO",
                            property_area: 45.0,
                            property_qtd_bedroom: 0,
                            property_qtd_bathroom: 1,
                            property_qtd_parking_spaces: 1,
                            property_purchase_value: 320000.00,
                            property_alias: "Sala comercial - AV BEIRA MAR NORTE, CENTRO"
                        }
                    ],
                    vehicles: [
                        {
                            vehicle_type: 1,
                            vehicle_type_desc: "Automóvel",
                            vehicle_fuel_type: 5,
                            vehicle_fuel_desc: "Flex",
                            vehicle_brand_id: "59",
                            vehicle_brand_name: "VOLKSWAGEN",
                            vehicle_model_id: "5940",
                            vehicle_model_name: "T-Cross 1.0 TSI Flex 12V 5p Aut.",
                            vehicle_year: "2023-01",
                            vehicle_purchase_date: "2023-05-15",
                            vehicle_purchase_value: 145000.00,
                            vehicle_alias: "T-Cross 2023"
                        },
                        {
                            vehicle_type: 3,
                            vehicle_type_desc: "Motocicleta",
                            vehicle_fuel_type: 5,
                            vehicle_fuel_desc: "Flex",
                            vehicle_brand_id: "26",
                            vehicle_brand_name: "HONDA",
                            vehicle_model_id: "2601",
                            vehicle_model_name: "CB 500F",
                            vehicle_year: "2022-01",
                            vehicle_purchase_date: "2022-03-20",
                            vehicle_purchase_value: 32000.00,
                            vehicle_alias: "Honda CB 500F"
                        }
                    ],
                    investments: [
                        {
                            asset_type: "CA",
                            asset_sub_type: "Conta Corrente",
                            asset_alias: "Conta Corrente Itaú",
                            asset_current_value: 15340.45,
                            asset_source: "imported_dirpf",
                            asset_ca_details: {
                                ca_rate_cdi: 0.0,
                                ca_monthly_costs: 29.90
                            },
                            asset_fin_issue: {
                                id: "68de78fa1a6d4c40982ec861",
                                inst_name: "Itaú"
                            }
                        },
                        {
                            asset_type: "CA",
                            asset_sub_type: "Conta Corrente",
                            asset_alias: "Conta Corrente Nubank",
                            asset_current_value: 8500.00,
                            asset_source: "imported_dirpf",
                            asset_ca_details: {
                                ca_rate_cdi: 100.0,
                                ca_monthly_costs: 0.0
                            },
                            asset_fin_issue: {
                                id: "68de78fa1a6d4c40982ec861",
                                inst_name: "Nubank"
                            }
                        },
                        {
                            asset_type: "SA",
                            asset_sub_type: "Poupança",
                            asset_alias: "Poupança Caixa",
                            asset_current_value: 25000.00,
                            asset_purchase_date: "2020-01-15",
                            asset_source: "imported_dirpf",
                            asset_sa_details: {
                                sa_recurring_value: 500.00
                            }
                        },
                        {
                            asset_type: "PB",
                            asset_sub_type: "CDB",
                            asset_alias: "CDB Banco Inter 110% CDI",
                            asset_current_value: 50000.00,
                            asset_purchase_date: "2024-01-10",
                            asset_due_date: "2027-01-10",
                            asset_source: "imported_dirpf",
                            asset_pb_details: {
                                pb_type: "CDB",
                                pb_rate_cdi: 110.0,
                                pb_rate_pre_annual: 0.0,
                                pb_issuing_bank: "Banco Inter",
                                pb_negotiating_bank: "Banco Inter"
                            },
                            dirpf_import_meta: {
                                fields_complete: true,
                                missing_fields: [],
                                extra_actions: [],
                                requires_user_action_to_register: false
                            }
                        },
                        {
                            asset_type: "PB",
                            asset_sub_type: "LCI",
                            asset_alias: "LCI Itaú 95% CDI",
                            asset_current_value: 80000.00,
                            asset_purchase_date: "2024-06-15",
                            asset_due_date: "2026-06-15",
                            asset_source: "imported_dirpf",
                            asset_pb_details: {
                                pb_type: "LCI",
                                pb_rate_cdi: 95.0,
                                pb_rate_pre_annual: 0.0,
                                pb_issuing_bank: "Itaú Unibanco",
                                pb_negotiating_bank: "Itaú Corretora"
                            }
                        },
                        {
                            asset_type: "PB",
                            asset_sub_type: "LCA",
                            asset_alias: "LCA Bradesco IPCA+ 5%",
                            asset_current_value: 35000.00,
                            asset_purchase_date: "2023-11-20",
                            asset_due_date: "2028-11-20",
                            asset_source: "imported_dirpf",
                            asset_pb_details: {
                                pb_type: "LCA",
                                pb_rate_cdi: 0.0,
                                pb_rate_pre_annual: 5.0,
                                pb_issuing_bank: "Bradesco",
                                pb_negotiating_bank: "Bradesco Corretora"
                            }
                        },
                        {
                            asset_type: "TB",
                            asset_sub_type: "Tesouro Direto",
                            asset_alias: "Tesouro IPCA+ 2029",
                            asset_current_value: 45000.00,
                            asset_purchase_date: "2023-03-10",
                            asset_due_date: "2029-05-15",
                            asset_source: "imported_dirpf",
                            asset_tb_details: {
                                tb_name: "Tesouro IPCA+ 2029",
                                tb_rate_pre_plus: 5.85,
                                tb_pu_value_prch: 3250.50
                            }
                        },
                        {
                            asset_type: "TB",
                            asset_sub_type: "Tesouro Direto",
                            asset_alias: "Tesouro Selic 2027",
                            asset_current_value: 30000.00,
                            asset_purchase_date: "2024-02-20",
                            asset_due_date: "2027-03-01",
                            asset_source: "imported_dirpf",
                            asset_tb_details: {
                                tb_name: "Tesouro Selic 2027",
                                tb_rate_pre_plus: 0.0,
                                tb_pu_value_prch: 14500.00
                            }
                        },
                        {
                            asset_type: "IF",
                            asset_sub_type: "Fundo",
                            asset_alias: "ITAU PRIVILEGE RENDA FIXA REFERENCIADO DI",
                            asset_current_value: 27290.93,
                            asset_purchase_date: "2022-08-15",
                            asset_source: "imported_dirpf",
                            asset_if_details: {
                                if_cnpj_fundo: "26.199.519/0001-34",
                                if_fund_type: "CVM",
                                if_qtd_prch: 2500.0,
                                if_value_prch: 25000.00,
                                if_value_vna: 10.92,
                                if_ticker_symbol: ""
                            }
                        },
                        {
                            asset_type: "IF",
                            asset_sub_type: "Fundo Imobiliário",
                            asset_alias: "HGLG11 - CSHG Logística FII",
                            asset_current_value: 18500.00,
                            asset_purchase_date: "2023-07-10",
                            asset_source: "imported_dirpf",
                            asset_if_details: {
                                if_cnpj_fundo: "",
                                if_fund_type: "B3",
                                if_qtd_prch: 100,
                                if_value_prch: 16000.00,
                                if_value_vna: 185.00,
                                if_ticker_symbol: "HGLG11"
                            }
                        },
                        {
                            asset_type: "ST",
                            asset_sub_type: "Ações",
                            asset_alias: "PETR4 - Petrobras PN",
                            asset_current_value: 42500.00,
                            asset_purchase_date: "2023-01-20",
                            asset_source: "imported_dirpf",
                            asset_st_details: {
                                st_ticker_symbol: "PETR4",
                                st_qtd_prch: 1000,
                                st_value_prch: 28000.00,
                                st_value_vna: 42.50
                            }
                        },
                        {
                            asset_type: "ST",
                            asset_sub_type: "Ações",
                            asset_alias: "VALE3 - Vale ON",
                            asset_current_value: 35000.00,
                            asset_purchase_date: "2022-11-15",
                            asset_source: "imported_dirpf",
                            asset_st_details: {
                                st_ticker_symbol: "VALE3",
                                st_qtd_prch: 500,
                                st_value_prch: 32000.00,
                                st_value_vna: 70.00
                            }
                        },
                        {
                            asset_type: "CM",
                            asset_sub_type: "Criptomoeda",
                            asset_alias: "Bitcoin (BTC)",
                            asset_current_value: 125000.00,
                            asset_purchase_date: "2021-06-15",
                            asset_source: "imported_dirpf",
                            asset_cm_details: {
                                cm_code: "BTC",
                                cm_name: "Bitcoin",
                                cm_quantity_prch: 0.25,
                                cm_value_prch: 75000.00
                            }
                        },
                        {
                            asset_type: "CM",
                            asset_sub_type: "Criptomoeda",
                            asset_alias: "Ethereum (ETH)",
                            asset_current_value: 28000.00,
                            asset_purchase_date: "2022-02-10",
                            asset_source: "imported_dirpf",
                            asset_cm_details: {
                                cm_code: "ETH",
                                cm_name: "Ethereum",
                                cm_quantity_prch: 2.5,
                                cm_value_prch: 20000.00
                            }
                        },
                        {
                            asset_type: "DB",
                            asset_sub_type: "Debênture",
                            asset_alias: "VALE24 - Debênture Vale",
                            asset_current_value: 55000.00,
                            asset_purchase_date: "2023-09-01",
                            asset_due_date: "2028-09-01",
                            asset_source: "imported_dirpf",
                            asset_db_details: {
                                db_code: "VALE24",
                                db_quantity_prch: 50,
                                db_pu_value_prch: 1050.00,
                                db_rate_anual: 12.5
                            }
                        },
                        {
                            asset_type: "PB",
                            asset_sub_type: "",
                            asset_alias: "CDB sem tipo (exemplo incompleto)",
                            asset_current_value: 0,
                            asset_source: "imported_dirpf",
                            asset_pb_details: {
                                pb_rate_cdi: 100.0
                            },
                            dirpf_import_meta: {
                                fields_complete: false,
                                missing_fields: ["asset_current_value", "asset_pb_details.pb_type"],
                                extra_actions: [],
                                requires_user_action_to_register: true
                            }
                        }
                    ],
                    debts: []
                }
            },
            error_message: null,
            file_size: 379752,
            steps: [
                {
                    step: "initializing",
                    step_name: "Inicializando importação",
                    started_at: "2026-03-11T11:46:24.487000",
                    completed_at: "2026-03-11T11:46:24.600000",
                    duration_seconds: 0.113,
                    status: "completed",
                    error_message: null,
                    details: null
                },
                {
                    step: "downloading_file",
                    step_name: "Baixando arquivo do S3",
                    started_at: "2026-03-11T11:46:24.743000",
                    completed_at: "2026-03-11T11:46:25.176000",
                    duration_seconds: 0.433449,
                    status: "completed",
                    error_message: null,
                    details: { file_size: 379752 }
                },
                {
                    step: "parsing_file",
                    step_name: "Extraindo texto do arquivo DIRPF",
                    started_at: "2026-03-11T11:46:25.686000",
                    completed_at: "2026-03-11T11:46:26.422000",
                    duration_seconds: 0.73606,
                    status: "completed",
                    error_message: null,
                    details: { text_length: 12957 }
                },
                {
                    step: "extraction",
                    step_name: "Extraindo dados do arquivo DIRPF",
                    started_at: "2026-03-11T11:46:26.677000",
                    completed_at: "2026-03-11T11:46:39.156000",
                    duration_seconds: 12.479292,
                    status: "completed",
                    error_message: null,
                    details: {
                        properties_count: 3,
                        vehicles_count: 2,
                        investments_count: 15,
                        debts_count: 3
                    }
                },
                {
                    step: "processing_assets",
                    step_name: "Processando investimentos",
                    started_at: "2026-03-11T11:46:39.411000",
                    completed_at: "2026-03-11T11:46:41.090000",
                    duration_seconds: 1.679831,
                    status: "completed",
                    error_message: null,
                    details: {
                        assets_created: 15,
                        assets_updated: 0,
                        assets_skipped: 0
                    }
                },
                {
                    step: "processing_properties",
                    step_name: "Processando imóveis",
                    started_at: "2026-03-11T11:46:41.346000",
                    completed_at: "2026-03-11T11:46:42.113000",
                    duration_seconds: 0.767679,
                    status: "completed",
                    error_message: null,
                    details: {
                        properties_created: 3,
                        properties_updated: 0,
                        properties_skipped: 0
                    }
                },
                {
                    step: "processing_vehicles",
                    step_name: "Processando veículos",
                    started_at: "2026-03-11T11:46:42.368000",
                    completed_at: "2026-03-11T11:46:42.623000",
                    duration_seconds: 0.255058,
                    status: "completed",
                    error_message: null,
                    details: {
                        vehicles_created: 2,
                        vehicles_skipped: 0
                    }
                },
                {
                    step: "finalizing",
                    step_name: "Finalizando importação",
                    started_at: "2026-03-11T11:46:42.878000",
                    completed_at: "2026-03-11T11:46:43.133000",
                    duration_seconds: 0.255212,
                    status: "completed",
                    error_message: null,
                    details: null
                }
            ]
        }
    ]
};

const USE_MOCK = (import.meta.env.VITE_MODE == "localhost");

const ACCEPTED_EXTENSIONS = ".pdf";

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD/MM/YYYY");
};

export default function ImportTaxSidebar({ importTaxOpen, setImportTaxOpen }: ITaxImportDialogProps) { 
    const auth = useAuthContext();
    const overlayPanelRef = useRef<OverlayPanel>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useRef<Toast>(null);
    const overlayDetailsPanelRef = useRef<OverlayPanel>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedImport, setSelectedImport] = useState<ITaxImport | null>(null);
    const [expandedRows, setExpandedRows] = useState<ITaxImport[]>([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedAssetToConfirm, setSelectedAssetToConfirm] = useState<ISelectedAssetToConfirm | null>(null);

    const handleConfirmAsset = (type: AssetFormType, data: IExtractedVehicle | IExtractedProperty | IExtractedInvestment) => {
        setSelectedAssetToConfirm({ type, data });
        setConfirmDialogOpen(true);
    };

    const handleConfirmSuccess = () => {
        setConfirmDialogOpen(false);
        setSelectedAssetToConfirm(null);
        toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Ativo confirmado com sucesso!",
            life: 3000
        });
        dataQuery.refetch();
    };

    const taxImportQueryEnabled =
        importTaxOpen && Boolean(auth.user.user_id && auth.token);

    const dataQuery = useQuery({
        queryKey: ['tax-import', auth.user.user_id],
        enabled: taxImportQueryEnabled,
        staleTime: 0,
        queryFn: async () => {
            if (USE_MOCK) {
                return MOCK_DATA;
            }
            const response = await axios.get<{ import_statuses: ITaxImport[] }>(
                `${import.meta.env.VITE_API_URL}/user/${auth.user.user_id}/dirpf/import`,
                { headers: { Authorization: auth.token } }
            );
            return response.data;
        }
    });

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "Selecione um arquivo para importar",
                life: 3000
            });
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            await axios.post(
                `${import.meta.env.VITE_API_URL}/user/${auth.user.user_id}/dirpf/import`,
                formData,
                {
                    headers: {
                        Authorization: auth.token
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = progressEvent.total 
                            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            : 0;
                        setUploadProgress(progress);
                    }
                }
            );

            toast.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Arquivo enviado com sucesso! O processamento será iniciado em breve.",
                life: 5000
            });

            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            overlayPanelRef.current?.hide();
            dataQuery.refetch();
        } catch (error) {
            const errorMessage = axios.isAxiosError(error) 
                ? error.response?.data?.message || "Erro ao enviar arquivo"
                : "Erro ao enviar arquivo";

            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const rowExpansionTemplate = (data: ITaxImport) => {
        const extractedData = data.stats?.extracted_data;
        
        if (!extractedData) {
            return (
                <div className="p-3 text-center text-color-secondary">
                    <i className="pi pi-info-circle mr-2"></i>
                    Nenhum dado extraído disponível para esta importação.
                </div>
            );
        }

        const hasProperties = extractedData.properties?.length > 0;
        const hasVehicles = extractedData.vehicles?.length > 0;
        const hasInvestments = extractedData.investments?.length > 0;

        if (!hasProperties && !hasVehicles && !hasInvestments) {
            return (
                <div className="p-3 text-center text-color-secondary">
                    <i className="pi pi-info-circle mr-2"></i>
                    Nenhum dado extraído disponível para esta importação.
                </div>
            );
        }

        return (
            <div className="p-3">
                <TabView>
                    {hasInvestments && (
                        <TabPanel header={`Investimentos (${extractedData.investments.length})`} leftIcon="pi pi-wallet mr-2">
                            <DataTable 
                                value={extractedData.investments} 
                                size="small"
                                stripedRows
                                emptyMessage="Nenhum investimento encontrado"
                            >
                                <Column
                                    header="Validação"
                                    style={{ width: "3.5rem", textAlign: "center" }}
                                    body={(row: IExtractedInvestment) => (
                                        <DirpfImportStatusCell meta={row.dirpf_import_meta} />
                                    )}
                                />
                                <Column 
                                    header="Ação"
                                    style={{ width: '100px' }}
                                    body={(row: IExtractedInvestment) => {
                                        const formType = mapAssetTypeToFormType(row.asset_type);
                                        if (!formType) return null;
                                        return (
                                            <Button 
                                                icon="pi pi-save" 
                                                size="small"
                                                rounded
                                                outlined
                                                tooltip="Salvar e confirmar cadastro na plataforma"
                                                tooltipOptions={{ position: 'top' }}
                                                onClick={() => handleConfirmAsset(formType, row)}
                                            />
                                        );
                                    }}
                                />
                                <Column field="asset_type" header="Tipo" style={{ width: '60px' }} />
                                <Column field="asset_sub_type" header="Subtipo" style={{ width: '120px' }} />
                                <Column field="asset_alias" header="Descrição" />
                                <Column 
                                    field="asset_current_value" 
                                    header="Valor Atual" 
                                    body={(row) => formatCurrency(row.asset_current_value)}
                                    style={{ textAlign: 'right', width: '130px' }}
                                />
                                <Column 
                                    header="Detalhes"
                                    body={(row: IExtractedInvestment) => {
                                        if (row.asset_pb_details) {
                                            const details = [];
                                            const cdi = row.asset_pb_details.pb_rate_cdi ?? 0;
                                            const pre = row.asset_pb_details.pb_rate_pre_annual ?? 0;
                                            if (cdi > 0) details.push(`${cdi}% CDI`);
                                            if (pre > 0) details.push(`IPCA+ ${pre}%`);
                                            if (row.asset_pb_details.pb_issuing_bank) details.push(row.asset_pb_details.pb_issuing_bank);
                                            return <span className="text-sm">{details.join(" | ")}</span>;
                                        }
                                        if (row.asset_tb_details) {
                                            return (
                                                <span className="text-sm">
                                                    {row.asset_tb_details.tb_name}
                                                    {row.asset_tb_details.tb_rate_pre_plus > 0 && ` | IPCA+ ${row.asset_tb_details.tb_rate_pre_plus}%`}
                                                </span>
                                            );
                                        }
                                        if (row.asset_if_details) {
                                            const details = [];
                                            if (row.asset_if_details.if_ticker_symbol) details.push(`Ticker: ${row.asset_if_details.if_ticker_symbol}`);
                                            if (row.asset_if_details.if_cnpj_fundo) details.push(`CNPJ: ${row.asset_if_details.if_cnpj_fundo}`);
                                            if (row.asset_if_details.if_qtd_prch > 0) details.push(`Qtd: ${row.asset_if_details.if_qtd_prch}`);
                                            return <span className="text-sm">{details.join(" | ")}</span>;
                                        }
                                        if (row.asset_st_details) {
                                            return (
                                                <span className="text-sm">
                                                    {row.asset_st_details.st_ticker_symbol} | Qtd: {row.asset_st_details.st_qtd_prch} | Cotação: {formatCurrency(row.asset_st_details.st_value_vna)}
                                                </span>
                                            );
                                        }
                                        if (row.asset_cm_details) {
                                            return (
                                                <span className="text-sm">
                                                    {row.asset_cm_details.cm_code} | Qtd: {row.asset_cm_details.cm_quantity_prch}
                                                </span>
                                            );
                                        }
                                        if (row.asset_db_details) {
                                            return (
                                                <span className="text-sm">
                                                    {row.asset_db_details.db_code} | Qtd: {row.asset_db_details.db_quantity_prch} | Taxa: {row.asset_db_details.db_rate_anual}% a.a.
                                                </span>
                                            );
                                        }
                                        if (row.asset_ca_details) {
                                            const details = [];
                                            if (row.asset_ca_details.ca_rate_cdi > 0) details.push(`CDI: ${row.asset_ca_details.ca_rate_cdi}%`);
                                            if (row.asset_ca_details.ca_monthly_costs > 0) details.push(`Custo: ${formatCurrency(row.asset_ca_details.ca_monthly_costs)}/mês`);
                                            return <span className="text-sm">{details.join(" | ") || "-"}</span>;
                                        }
                                        if (row.asset_sa_details) {
                                            return (
                                                <span className="text-sm">
                                                    {row.asset_sa_details.sa_recurring_value > 0 && `Aporte mensal: ${formatCurrency(row.asset_sa_details.sa_recurring_value)}`}
                                                </span>
                                            );
                                        }
                                        return "-";
                                    }}
                                />
                            </DataTable>
                        </TabPanel>
                    )}
                    
                    {hasProperties && (
                        <TabPanel header={`Imóveis (${extractedData.properties.length})`} leftIcon="pi pi-home mr-2">
                            <DataTable 
                                value={extractedData.properties} 
                                size="small"
                                stripedRows
                                emptyMessage="Nenhum imóvel encontrado"
                            >
                                <Column
                                    header="Validação"
                                    style={{ width: "3.5rem", textAlign: "center" }}
                                    body={(row: IExtractedProperty) => (
                                        <DirpfImportStatusCell meta={row.dirpf_import_meta} />
                                    )}
                                />
                                <Column 
                                    header="Ação"
                                    style={{ width: '100px' }}
                                    body={(row: IExtractedProperty) => (
                                        <Button 
                                            icon="pi pi-save" 
                                            size="small"
                                            rounded
                                            outlined
                                            tooltip="Salvar e confirmar cadastro na plataforma"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => handleConfirmAsset("property", row)}
                                        />
                                    )}
                                />
                                <Column field="property_type_desc" header="Tipo" />
                                <Column field="property_alias" header="Descrição" />
                                <Column 
                                    header="Endereço"
                                    body={(row: IExtractedProperty) => (
                                        <span>
                                            {row.property_place}
                                            {row.property_number && `, ${row.property_number}`}
                                            {row.property_complement && ` - ${row.property_complement}`}
                                        </span>
                                    )}
                                />
                                <Column 
                                    header="Cidade/UF"
                                    body={(row: IExtractedProperty) => `${row.property_city}/${row.property_state}`}
                                />
                                <Column 
                                    field="property_area" 
                                    header="Área (m²)"
                                    body={(row) => row.property_area > 0 ? `${row.property_area} m²` : "-"}
                                />
                                <Column 
                                    field="property_purchase_date" 
                                    header="Data Compra"
                                    body={(row) => formatDate(row.property_purchase_date)}
                                />
                                <Column 
                                    field="property_purchase_value" 
                                    header="Valor Compra"
                                    body={(row) => formatCurrency(row.property_purchase_value)}
                                    style={{ textAlign: 'right' }}
                                />
                            </DataTable>
                        </TabPanel>
                    )}

                    {hasVehicles && (
                        <TabPanel header={`Veículos (${extractedData.vehicles.length})`} leftIcon="pi pi-car mr-2">
                            <DataTable 
                                value={extractedData.vehicles} 
                                size="small"
                                stripedRows
                                emptyMessage="Nenhum veículo encontrado"
                            >
                                <Column
                                    header="Validação"
                                    style={{ width: "3.5rem", textAlign: "center" }}
                                    body={(row: IExtractedVehicle) => (
                                        <DirpfImportStatusCell meta={row.dirpf_import_meta} />
                                    )}
                                />
                                <Column 
                                    header="Ação"
                                    style={{ width: '100px' }}
                                    body={(row: IExtractedVehicle) => (
                                        <Button 
                                            icon="pi pi-save" 
                                            size="small"
                                            rounded
                                            outlined
                                            tooltip="Salvar e confirmar cadastro na plataforma"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => handleConfirmAsset("vehicle", row)}
                                        />
                                    )}
                                />
                                <Column field="vehicle_type_desc" header="Tipo" />
                                <Column field="vehicle_brand_name" header="Marca" />
                                <Column field="vehicle_model_name" header="Modelo" />
                                <Column 
                                    field="vehicle_year" 
                                    header="Ano"
                                    body={(row) => row.vehicle_year?.split("-")[0] || "-"}
                                />
                                <Column field="vehicle_fuel_desc" header="Combustível" />
                                <Column field="vehicle_alias" header="Apelido" />
                                <Column 
                                    field="vehicle_purchase_date" 
                                    header="Data Compra"
                                    body={(row) => formatDate(row.vehicle_purchase_date)}
                                />
                                <Column 
                                    field="vehicle_purchase_value" 
                                    header="Valor Compra"
                                    body={(row) => formatCurrency(row.vehicle_purchase_value)}
                                    style={{ textAlign: 'right' }}
                                />
                            </DataTable>
                        </TabPanel>
                    )}
                </TabView>
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog 
                header="Importação DIRPF" 
                visible={importTaxOpen} 
                onHide={() => setImportTaxOpen(false)}
                style={{ width: '80vw', maxWidth: '1000px' }}
            >
                <div className="flex flex-column gap-3 mb-4">
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button 
                            icon="pi pi-upload" 
                            label="Nova Importação"
                            onClick={(event) => overlayPanelRef.current?.toggle(event)} 
                        />
                    </div>
                    
                    <OverlayPanel ref={overlayPanelRef} style={{ width: '400px' }}>
                        <div className="flex flex-column gap-3">
                            <h4 className="m-0">Importar Declaração DIRPF</h4>
                            <p className="text-sm text-color-secondary m-0">
                                Selecione o arquivo PDF da sua declaração do Imposto de Renda.
                            </p>
                            
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_EXTENSIONS}
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            
                            <div 
                                className="border-2 border-dashed border-round p-4 text-center cursor-pointer hover:surface-hover transition-colors transition-duration-200"
                                style={{ borderColor: 'var(--primary-color)' }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {selectedFile ? (
                                    <div className="flex flex-column align-items-center gap-2">
                                        <i className="pi pi-file text-4xl text-primary"></i>
                                        <span className="font-semibold text-overflow-ellipsis overflow-hidden" style={{ maxWidth: '100%' }}>
                                            {selectedFile.name}
                                        </span>
                                        <span className="text-sm text-color-secondary">
                                            {formatFileSize(selectedFile.size)}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex flex-column align-items-center gap-2">
                                        <i className="pi pi-cloud-upload text-4xl text-primary"></i>
                                        <span className="font-semibold">Clique para selecionar o arquivo PDF</span>
                                    </div>
                                )}
                            </div>

                            {isUploading && (
                                <ProgressBar value={uploadProgress} />
                            )}

                            <div className="flex gap-2 justify-content-end">
                                <Button 
                                    label="Cancelar" 
                                    severity="secondary"
                                    outlined
                                    onClick={() => {
                                        setSelectedFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = "";
                                        }
                                        overlayPanelRef.current?.hide();
                                    }} 
                                    disabled={isUploading}
                                />
                                <Button 
                                    label={isUploading ? "Enviando..." : "Importar"}
                                    icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
                                    onClick={handleUpload}
                                    disabled={!selectedFile || isUploading}
                                />
                            </div>
                        </div>
                    </OverlayPanel>
                </div>

                <DataTable
                    value={dataQuery.data?.import_statuses ?? []}
                    resizableColumns
                    loading={dataQuery.isLoading}
                    emptyMessage="Nenhuma importação encontrada"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data as ITaxImport[])}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="id"
                >
                    <Column
                        style={{ width: '3rem' }}
                        body={(rowData: ITaxImport) => {
                            const isExpanded = expandedRows.some((r) => r.id === rowData.id);
                            return (
                                <button
                                    type="button"
                                    className="p-row-toggler p-link inline-flex align-items-center justify-content-center border-none bg-transparent cursor-pointer"
                                    aria-expanded={isExpanded}
                                    onClick={() => {
                                        setExpandedRows((prev) => {
                                            if (prev.some((r) => r.id === rowData.id)) {
                                                return prev.filter((r) => r.id !== rowData.id);
                                            }
                                            return [...prev, rowData];
                                        });
                                    }}
                                >
                                    <i className={`pi ${isExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} />
                                </button>
                            );
                        }}
                    />
                    <Column field="filename" header="Nome do arquivo" />
                    <Column field="status" header="Status" body={(rowData) => <Badge value={rowData.status == "completed" ? "Concluído" : "Em andamento"} severity={rowData.status == "completed" ? "success" : "info"} />} />
                    <Column field="created_at" header="Data de criação" body={(rowData) => moment(rowData.created_at).format("DD/MM/YYYY HH:mm:ss")} />
                    <Column field="started_at" header="Data de início" body={(rowData) => moment(rowData.started_at).format("DD/MM/YYYY HH:mm:ss")} />
                    <Column field="completed_at" header="Data de conclusão" body={(rowData) => moment(rowData.completed_at).format("DD/MM/YYYY HH:mm:ss")} />
                    <Column field="stats.assets_created" header="Ativos criados" />
                    <Column field="stats.assets_updated" header="Ativos atualizados" />
                    <Column field="stats.assets_skipped" header="Ativos ignorados" />
                    <Column field="stats.properties_created" header="Imóveis criados" />
                    <Column field="stats.properties_updated" header="Imóveis atualizados" />
                    <Column field="stats.properties_skipped" header="Imóveis ignorados" />
                    <Column field="stats.vehicles_created" header="Veículos criados" />
                    <Column field="stats.vehicles_skipped" header="Veículos ignorados" />
                    <Column field="error_message" header="Mensagem de erro" />
                    <Column 
                        field="file_size" 
                        header="Tamanho" 
                        body={(rowData) => formatFileSize(rowData.file_size)}
                    />
                    <Column frozen alignFrozen="right" field="Detalhes" body={(rowData: ITaxImport) => {
                        return (
                            <div className="flex flex-row gap-2 justify-content-end">
                                <Button icon="pi pi-eye" onClick={(event) => {
                                    overlayDetailsPanelRef.current?.toggle(event);
                                    setSelectedImport(rowData);
                                }} />
                            </div>
                        )
                    }} />
                </DataTable>
                <OverlayPanel ref={overlayDetailsPanelRef} style={{ width: '600px' }}>
                    <div className="flex flex-column gap-3">
                        <h4 className="m-0">Detalhes da importação</h4>
                        <p className="text-sm text-color-secondary m-0">
                            {selectedImport?.error_message}
                        </p>
                        <DataTable value={selectedImport?.steps ?? []}>
                            <Column field="step" header="Passo" />
                            <Column field="step_name" header="Nome do passo" />
                            <Column field="started_at" header="Data de início" body={(rowData) => moment(rowData.started_at).format("DD/MM/YYYY HH:mm:ss")} />
                            <Column field="completed_at" header="Data de conclusão" body={(rowData) => moment(rowData.completed_at).format("DD/MM/YYYY HH:mm:ss")} />
                            <Column field="duration_seconds" header="Duração" body={(rowData) => moment.duration(rowData.duration_seconds, "seconds").humanize()} />
                        </DataTable>
                        <Button icon="pi pi-times" onClick={() => overlayDetailsPanelRef.current?.hide()} />
                    </div>
                </OverlayPanel>
            </Dialog>

            <Dialog
                header={selectedAssetToConfirm ? getFormTitle(selectedAssetToConfirm.type) : "Confirmar Ativo"}
                visible={confirmDialogOpen}
                onHide={() => {
                    setConfirmDialogOpen(false);
                    setSelectedAssetToConfirm(null);
                }}
                style={{ width: '50rem' }}
                modal
            >
                {selectedAssetToConfirm?.type === "vehicle" && (
                    <VehicleHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedVehicle}
                    />
                )}
                {selectedAssetToConfirm?.type === "property" && (
                    <PropertyHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedProperty}
                    />
                )}
                {selectedAssetToConfirm?.type === "stock" && (
                    <StockHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "crypto" && (
                    <CriptoHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "fund" && (
                    <FundHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "debenture" && (
                    <DebentureHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "private_bonds" && (
                    <PrivateBondsHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "treasury" && (
                    <TreasureBoundsHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "savings" && (
                    <SavingAccountHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "checking" && (
                    <CheckingAccoundHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "currency" && (
                    <CurrencyHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
                {selectedAssetToConfirm?.type === "customized" && (
                    <CustomizedHandleForm
                        onSuccessRegister={handleConfirmSuccess}
                        goBack={() => {
                            setConfirmDialogOpen(false);
                            setSelectedAssetToConfirm(null);
                        }}
                        initialData={selectedAssetToConfirm.data as IExtractedInvestment}
                    />
                )}
            </Dialog>
        </>
    );
}
